import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/auth';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import { useResolveNavigationId, useSearchNavigationNames } from '@/domain/navigation';

import {
    navigationMatchToQuickSearchItem,
    parseNavigationId,
    shouldResolveNavigationId,
    shouldSearchNavigationNames,
} from './quickSearchHelpers';
import type { QuickSearchItem } from './types';

export interface UseQuickSearchRemoteItemsResult {
    items: QuickSearchItem[];
    isResolving: boolean;
    navigationId: number | null;
    canSearchNames: boolean;
}

export function useQuickSearchRemoteItems(
    isOpen: boolean,
    search: string
): UseQuickSearchRemoteItemsResult {
    const { t } = useTranslation();
    const { isOptoScaleAdmin } = useAuth();
    const { isDemoMode } = useDemoMode();

    const navigationId = parseNavigationId(search);
    const { data: resolvedMatches, isFetching: isResolvingId } = useResolveNavigationId(
        navigationId,
        {
            enabled: shouldResolveNavigationId({ isOpen, isDemoMode, id: navigationId }),
        }
    );
    const canSearchNames = shouldSearchNavigationNames({
        isOpen,
        isDemoMode,
        isOptoScaleAdmin,
        query: search,
    });
    const { data: nameSearchMatches, isFetching: isSearchingNames } = useSearchNavigationNames(
        search,
        { enabled: canSearchNames }
    );

    const items = useMemo<QuickSearchItem[]>(() => {
        if (isDemoMode) {
            return [];
        }
        const isIdMatch = navigationId != null;
        const matches = isIdMatch ? resolvedMatches : nameSearchMatches;
        return matches
            .map((match) =>
                navigationMatchToQuickSearchItem(match, {
                    isOptoScaleAdmin,
                    isIdMatch,
                    t: (key, options) => t(key, options),
                })
            )
            .filter((item): item is QuickSearchItem => item != null);
    }, [isDemoMode, navigationId, resolvedMatches, nameSearchMatches, isOptoScaleAdmin, t]);

    return {
        items,
        isResolving: isResolvingId || isSearchingNames,
        navigationId,
        canSearchNames,
    };
}
